import { travelerFromFirestore } from '@parallel-fluidics/shared/models/travelers';
import fetchProduct from 'features/products/apis/fetchProduct';
import findProductByPart from 'features/products/apis/findProductByPart';
import {
  doc,
  getDoc,
  getFirestore,
} from 'firebase/firestore';

import { getBatchNumber } from './utils';

/**
 * Load everything needed to prefill a product label from a traveler:
 * the traveler itself, its catalog product (by productDocId, falling back to a
 * (project, pPart) lookup), and the batch number computed from its order.
 * Product/order problems are non-fatal — they surface as warnings so the user
 * can fill the fields in manually.
 */
export default async function loadLabelData(travelerId) {
  const db = getFirestore();
  const travelerSnap = await getDoc(doc(db, 'travelers', travelerId));
  if (!travelerSnap.exists()) {
    throw new Error(`Traveler not found: ${travelerId}`);
  }
  const traveler = travelerFromFirestore(travelerSnap);

  const warnings = [];

  const [product, batch] = await Promise.all([
    (async () => {
      try {
        if (traveler.productDocId) {
          return await fetchProduct(traveler.productDocId);
        }
        const match = await findProductByPart(traveler.project, traveler.pPart);
        if (!match) {
          warnings.push(`No catalog product found for ${traveler.project}-${traveler.pPart}. Enter the product name and SKU manually.`);
        }
        return match?.product ?? null;
      } catch (error) {
        warnings.push(`Failed to load product: ${error.message}`);
        return null;
      }
    })(),
    (async () => {
      if (!traveler.order) {
        warnings.push('Traveler has no order — enter the batch number manually.');
        return '';
      }
      try {
        const orderSnap = await getDoc(doc(db, 'orders', traveler.order));
        if (!orderSnap.exists()) {
          warnings.push(`Order not found: ${traveler.order}. Enter the batch number manually.`);
          return '';
        }
        const batchNumber = getBatchNumber(orderSnap.data(), traveler.lineItem);
        if (!batchNumber) {
          warnings.push('Could not compute the batch number from the order — enter it manually.');
        }
        return batchNumber;
      } catch (error) {
        warnings.push(`Failed to load order: ${error.message}`);
        return '';
      }
    })(),
  ]);

  return {
    traveler, product, batch, warnings,
  };
}
